import { useAuthStore } from '@/store/auth'

/**
 * Hook to check if the current user can modify (edit/delete) a match
 * 
 * Authorization rules:
 * 1. Platform owner can modify all matches
 * 2. Club admin can modify all matches in their club
 * 3. Players can only modify matches they participated in
 * 4. Unauthenticated users cannot modify any matches
 */
export function useMatchPermissions() {
  const { user, isPlatformOwner, isClubAdmin } = useAuthStore()

  /**
   * Check if user can modify a specific match
   * @param clubId - The club ID that owns the series
   * @param playerAId - First player's ID in the match
   * @param playerBId - Second player's ID in the match
   * @returns true if user can modify the match, false otherwise
   */
  const canModifyMatch = (clubId: string, playerAId: string, playerBId: string): boolean => {
    // Not authenticated = no permissions
    if (!user) {
      return false
    }

    // Platform owner can modify all matches
    if (isPlatformOwner()) {
      return true
    }

    // Club admin can modify all matches in their club
    if (isClubAdmin(clubId)) {
      return true
    }

    // Players can only modify their own matches
    const currentPlayerId = user.playerId
    return currentPlayerId === playerAId || currentPlayerId === playerBId
  }

  return {
    canModifyMatch,
    isAuthenticated: !!user,
    currentPlayerId: user?.playerId
  }
}
